import { MenuItem, TextField } from "@mui/material";
import { useState } from "react";
import { CatalogService } from "@services/catalog/catalogService";
import { CatalogDimension, CatalogEntityType, ItemUsage } from "@models/catalog.model";
import { FormDialog } from "./FormDialog";
import { DimensionValuesEditor } from "./DimensionValuesEditor";
import { UsagePanel, rewriteNotice } from "./usage";

// Dimension edits key off the CODE (that's the stable identifier used in metric.dimension_keys
// and AttributeValue.dimension), but the write API takes the row id — so the editor carries both.
export interface DimensionEdit extends CatalogDimension {
  id: number;
  reference_entity_type_id: number | null;
}

export function DimensionDialog({
  verticalId, entityTypes, usage, initial, onClose, onSaved,
}: {
  verticalId: number;
  entityTypes: CatalogEntityType[];
  usage?: ItemUsage; // what a code change would break (MPN-1781)
  initial: DimensionEdit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const svc = new CatalogService();
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [refId, setRefId] = useState<number | "">(initial?.reference_entity_type_id ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Value rows write immediately, so dismissing with Cancel still has to refresh the caller —
  // otherwise the axis's value chips on the catalog page keep showing the pre-edit list.
  const [valuesChanged, setValuesChanged] = useState(false);

  const close = () => (valuesChanged ? onSaved() : onClose());

  // A rename fans out to metric keys, field-map pins, loaded data AND rule text (MPN-1780), so
  // nothing blocks; a `rules` tier just says which contracts get rewritten (MPN-1781).
  const renaming = !!initial && code !== initial.code;
  const notice = renaming && usage?.tier === "rules" ? rewriteNotice(usage, `dimension.${code}`) : undefined;

  // A dimension's value domain is usually a reference entity type, but any same-vertical type is allowed.
  const refOptions = [...entityTypes].sort(
    (a, b) => Number(b.is_reference) - Number(a.is_reference),
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      vertical_id: verticalId, code, name, description,
      reference_entity_type_id: refId === "" ? null : refId,
    };
    try {
      if (initial) await svc.updateDimension(initial.id, payload);
      else await svc.createDimension(payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open
      title={initial ? "Edit dimension" : "New dimension"}
      // Wider than its siblings: this one carries the values table, and at "sm" the Order
      // column was clipped to a sliver.
      maxWidth={initial ? "md" : "sm"}
      onClose={close}
      onSave={save}
      saving={saving}
      error={error}
      canSave={!!code.trim() && !!name.trim()}
    >
      {/* The code is spliced into conversion-rule text as `dimension.<code>`, so it has to lex as one
          identifier — `dimension.Enrollment Month` broke every pricesheet-item save (MPN-1776). */}
      <TextField label="Code" value={code}
        onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
        helperText="Lowercase letters, digits and underscores, e.g. period, enrollment_month. Copied into field maps, data and rules; a change is carried to all of them." size="small" autoFocus />
      {renaming && usage && <UsagePanel usage={usage} notice={notice} />}
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)}
        helperText="A label only — always safe to change" size="small" />
      <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
        size="small" multiline minRows={2} />
      <TextField select label="Reference entity type" value={refId}
        onChange={(e) => setRefId(e.target.value === "" ? "" : Number(e.target.value))}
        helperText="The type whose instances are this axis's values (optional)" size="small">
        <MenuItem value="">— none —</MenuItem>
        {refOptions.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.name} ({t.code}){t.is_reference ? " · ref" : ""}
          </MenuItem>
        ))}
      </TextField>

      {/* Values belong to a saved axis, so they only appear once one exists. Editing them is
          immediate rather than part of the dialog's save — each row is its own write, and the
          API's alias collision errors are worth showing per row rather than at the bottom. */}
      {initial && (
        <DimensionValuesEditor
          dimensionId={initial.id}
          onChanged={() => setValuesChanged(true)}
        />
      )}
    </FormDialog>
  );
}
